import { useCallback, useContext, useMemo } from 'react';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import CardContext from 'contexts/CardContext';
import ToastContext from 'contexts/ToastContext';
import useDateScope from 'hooks/useDateScope';
import useFilterInput from 'hooks/useFilterInput';
import visTypeCheckers from 'types/visTypeCheckers';
import exportGrid, { GridExportParams } from '../api/exportGrid';
import { GridApi } from 'ag-grid-community';
import GridContext from 'contexts/GridContext';
import ExportGridContext from '../context/ExportGridContext';
import { DefaultDatasetFiltersContext } from '../../../contextProviders/SplashScreenProviders/DatasetProviders/DefaultDatasetFiltersProvider';
import FilterPlatesContext from '../../../contexts/FilterPlatesContext';
import { TOTALS_QUERY } from '../gridConstants';
import getBaseViewFields from '../getBaseViewFields';
import BaseViewsContext from '../../../contexts/BaseViewsContext';
import useGetFieldLabel from '../../../hooks/useGetFieldLabel';

const GRID_MAX_DOCUMENTS_EXPORT_COUNT = 10000;

const useExportGrid = ({
  exportName,
  overrideGridApi,
  exportHandle,
}: {
  exportName?: string;
  overrideGridApi?: GridApi;
  exportHandle?: DataTable.ExportHandle;
}) => {
  const { gridApi, isLoadingData, baseViewOverride, gridTotalsQueryOverride } =
    useContext(GridContext);
  const { baseViews, ensureBaseViewIsPersisted } = useContext(BaseViewsContext);
  const { chartDefinition } = useContext(CardContext);
  const { api } = useContext(CloudFunctionClientContext);
  const { showToast } = useContext(ToastContext);
  const {
    onExportRequestStarted,
    onExportRequestResponse,
    onExportRequestFailed,
  } = useContext(ExportGridContext);
  const { getFieldLabel } = useGetFieldLabel();
  const dateScope = useDateScope({});
  const { filterInput: baseFilterInput } = useFilterInput({});
  const { defaultDatasetFiltersLookup } = useContext(
    DefaultDatasetFiltersContext,
  );
  const { isDefaultFiltersDisabled } = useContext(FilterPlatesContext);
  const dataset = useMemo(() => {
    if (
      visTypeCheckers.isV5ChartDef(chartDefinition) &&
      chartDefinition.simpleGridConfig
    ) {
      return chartDefinition.simpleGridConfig.dataType;
    }

    return undefined;
  }, [chartDefinition]);
  const filterInput = useMemo((): FilterInput[] => {
    if (!dataset) {
      return [];
    }
    const result: FilterInput[] = [baseFilterInput];
    if (!isDefaultFiltersDisabled) {
      const f = defaultDatasetFiltersLookup[dataset];
      if (f) {
        result.push(f.filterInput);
      }
    }

    return result;
  }, [
    dataset,
    baseFilterInput,
    isDefaultFiltersDisabled,
    defaultDatasetFiltersLookup,
  ]);

  const gridApiToUse = overrideGridApi || (gridApi as GridApi);

  const datasource = gridApiToUse?.getGridOption('serverSideDatasource') as
    | DataSource
    | undefined;
  const documentCount = exportHandle
    ? exportHandle.totalRowCount
    : datasource?.searchResult?.count;
  const isDocumentCountLoaded = !isLoadingData && documentCount !== undefined;

  const isDocumentCountExceeded = documentCount
    ? documentCount >= GRID_MAX_DOCUMENTS_EXPORT_COUNT
    : false;

  const exportSimpleGrid = useCallback(async () => {
    if (!gridApi && !overrideGridApi && !exportHandle) {
      const e = new Error();
      e.name = 'Simple Grid Export: Grid API Missing';
      throw e;
    }

    if (isDocumentCountExceeded || !isDocumentCountLoaded) {
      return;
    }

    const transformExportSortBy = (chartSort: SimpleGridSort[]) => {
      const result: GridSortField[] = [];

      if (chartSort) {
        chartSort.forEach((s) => {
          result.push({ field: s.field, sort: s.direction });
        });
      }

      return result;
    };

    const exportParams = ((): GridExportParams | undefined => {
      if (
        visTypeCheckers.isV5ChartDef(chartDefinition) &&
        chartDefinition.simpleGridConfig
      ) {
        const data_type = chartDefinition.simpleGridConfig.dataType;
        const name = exportName ? exportName : data_type;
        const filters = filterInput;
        const date_scope = dateScope;
        const sort_by = transformExportSortBy(
          chartDefinition.simpleGridConfig.sort,
        );

        const baseView = baseViewOverride
          ? baseViewOverride
          : baseViews[data_type];
        if (!baseView) {
          const e = new Error();
          e.name = 'Simple Grid Export: No base view found';
          e.message = 'for ' + data_type;
          throw e;
        }

        const include_fields: { label: string; field: string }[] = (() => {
          const { fields, fieldAliases } = chartDefinition.simpleGridConfig;

          if (!fieldAliases) {
            return fields.map((f) => ({
              label: getFieldLabel({ field: f, dataType: data_type }),
              field: f,
            }));
          }

          return fieldAliases.map((alias) => {
            return {
              field: alias.field,
              label: alias.alias,
            };
          });
        })();

        const totals_fields = getBaseViewFields(baseView)
          .filter(
            (c) => c.aggFunc && include_fields.some((f) => f.field === c.field),
          )
          .map(
            (c): GridAggregateField => ({
              field: c.field,
              aggFunc: c.aggFunc,
            }),
          );

        const totals_query = gridTotalsQueryOverride
          ? gridTotalsQueryOverride
          : TOTALS_QUERY;

        return {
          name,
          data_type,
          filters,
          date_scope,
          sort_by,
          include_fields,
          totals_query,
          totals_fields,
          baseView,
          api,
        };
      }
      return undefined;
    })();

    if (!exportParams) {
      throw new Error('Simple grid config is not defined');
    }

    await ensureBaseViewIsPersisted(exportParams.baseView);
    onExportRequestStarted(exportParams.name);
    const response = await exportGrid(exportParams);

    if (response.ok && response.data) {
      onExportRequestResponse(response.data.exportDocumentId);
    } else {
      onExportRequestFailed();
      showToast('Error exporting grid');
    }
  }, [
    api,
    baseViewOverride,
    baseViews,
    chartDefinition,
    dateScope,
    ensureBaseViewIsPersisted,
    exportHandle,
    exportName,
    filterInput,
    getFieldLabel,
    gridApi,
    gridTotalsQueryOverride,
    isDocumentCountExceeded,
    isDocumentCountLoaded,
    onExportRequestFailed,
    onExportRequestResponse,
    onExportRequestStarted,
    overrideGridApi,
    showToast,
  ]);

  return {
    exportSimpleGrid,
    isDocumentCountExceeded,
    isDocumentCountLoaded,
  };
};

export default useExportGrid;
